import React, { useEffect, useCallback } from 'react';
import InteractiveField from '../common/InteractiveField';
import MultilineField from '../common/MultilineField';
import DateField from '../common/DateField';
import { convertAmountToWords, getRublesForm } from '../../utils/currency';
import { getHandoverActNumber } from '../../utils/attachmentNumbers';

/**
 * Функция склонения слова "экземпляр"
 */
const pluralizeExemplars = (count) => {
    const num = parseInt(count, 10);
    if (!num || isNaN(num)) return 'экземпляров';
    
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'экземпляров';
    }
    
    if (lastDigit === 1) {
        return 'экземпляр';
    }
    
    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'экземпляра';
    }
    
    return 'экземпляров';
};

/**
 * Компонент "Акт приема-передачи жилого помещения" (Приложение № 1)
 * УПРОЩЕННАЯ ВЕРСИЯ - соответствует реальной практике
 * Заполняется при заключении договора
 */
const HandoverActSection = ({ contractData, onChange, showValidation = false }) => {
    // Безопасная деструктуризация данных из договора
    const {
        header = {},
        parties = {},
        property = {},
        term = {},
        payment = {}
    } = contractData || {};

    const { securityDeposit: mainContractSecurityPayment = {} } = payment;

    // Обработчики изменения полей
    const handleHeaderChange = (field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                header: {
                    ...header,
                    [field]: value
                }
            });
        }
    };

    const handlePartiesChange = (field, value) => {
        if (!onChange) return;

        const newParties = { ...parties, [field]: value };
        const signatures = contractData.signatures || { landlord: {}, tenant: {} };
        const updated = {
            ...contractData,
            parties: newParties
        };

        // Двусторонняя синхронизация паспортных данных с разделом "Адреса и реквизиты Сторон"
        if (field === 'landlordPassportSerial' || field === 'landlordPassportNumber') {
            updated.signatures = {
                ...signatures,
                landlord: {
                    ...signatures.landlord,
                    passportSerial: field === 'landlordPassportSerial' ? value : newParties.landlordPassportSerial,
                    passportNumber: field === 'landlordPassportNumber' ? value : newParties.landlordPassportNumber
                }
            };
        }

        if (field === 'tenantPassportSerial' || field === 'tenantPassportNumber') {
            updated.signatures = {
                ...signatures,
                tenant: {
                    ...signatures.tenant,
                    passportSerial: field === 'tenantPassportSerial' ? value : newParties.tenantPassportSerial,
                    passportNumber: field === 'tenantPassportNumber' ? value : newParties.tenantPassportNumber
                }
            };
        }

        onChange(updated);
    };

    const handlePropertyChange = (field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                property: {
                    ...property,
                    [field]: value
                }
            });
        }
    };

    const handlePropertyToggle = (field) => {
        if (onChange) {
            onChange({
                ...contractData,
                property: {
                    ...property,
                    [field]: !property[field]
                }
            });
        }
    };

    const handlePaymentChange = useCallback((field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                payment: {
                    ...payment,
                    securityDeposit: {
                        ...mainContractSecurityPayment,
                        [field]: value,
                    },
                },
            });
        }
    }, [onChange, contractData, payment, mainContractSecurityPayment]);

    // Эффект для автоматического обновления текстового представления суммы обеспечительного платежа
    useEffect(() => {
        if (mainContractSecurityPayment.enabled && mainContractSecurityPayment.amount) {
            const words = convertAmountToWords(mainContractSecurityPayment.amount);
            if (words && words !== mainContractSecurityPayment.amountWords) {
                handlePaymentChange('amountWords', words);
            }
        }
    }, [mainContractSecurityPayment.enabled, mainContractSecurityPayment.amount, mainContractSecurityPayment.amountWords, handlePaymentChange]);

    // УПРОЩЕННАЯ структура данных для акта
    const rawHandoverItems = property.handoverItems || {};
    
    const safeHandoverItems = {
        // Пункт 3: Результат приемки (ВСЕГДА включен)
        acceptance: rawHandoverItems.acceptance || {
            suitableForLiving: true,
            deficiencies: null,
            defectsRepairDeadline: null
        },
        // Пункт 4: Состояние Помещения, оборудования и имущества (ОПЦИОНАЛЬНО)
        propertyCondition: {
            enabled: rawHandoverItems.propertyCondition?.enabled || false,
            apartmentEnabled: rawHandoverItems.propertyCondition?.apartmentEnabled !== undefined 
                ? rawHandoverItems.propertyCondition.apartmentEnabled 
                : true,  // По умолчанию включено
            apartment: rawHandoverItems.propertyCondition?.apartment || '',
            equipmentEnabled: rawHandoverItems.propertyCondition?.equipmentEnabled || false,
            equipment: rawHandoverItems.propertyCondition?.equipment || '',
            appliancesEnabled: rawHandoverItems.propertyCondition?.appliancesEnabled || false,
            appliances: rawHandoverItems.propertyCondition?.appliances || '',
            furnitureEnabled: rawHandoverItems.propertyCondition?.furnitureEnabled || false,
            furniture: rawHandoverItems.propertyCondition?.furniture || ''
        },
        // Пункт 5: Приемка ключей и документации (ОПЦИОНАЛЬНО)
        keysAndDocuments: rawHandoverItems.keysAndDocuments || {
            enabled: false,
            keys: { count: '1' },
            documentation: null
        },
        // Пункт 6: Показания приборов учета (ОПЦИОНАЛЬНО)
        meterReadings: rawHandoverItems.meterReadings || {
            enabled: false,
            electricity: null,
            hotWater: null,
            coldWater: null,
            gas: null
        },
    };

    const handleHandoverItemsChange = (newHandoverItems) => {
        // Проверка: если ни один подпункт состояния не включен, выключаем весь раздел
        if (newHandoverItems.propertyCondition?.enabled) {
            const hasAnyEnabled = newHandoverItems.propertyCondition.apartmentEnabled || 
                                 newHandoverItems.propertyCondition.equipmentEnabled || 
                                 newHandoverItems.propertyCondition.appliancesEnabled || 
                                 newHandoverItems.propertyCondition.furnitureEnabled;
            
            if (!hasAnyEnabled) {
                newHandoverItems.propertyCondition.enabled = false;
            }
        }
        handlePropertyChange('handoverItems', newHandoverItems);
    };

    // Обработчики для пункта 3 - Результат приемки
    const handleSuitableForLivingChange = (value) => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            acceptance: {
                ...safeHandoverItems.acceptance,
                suitableForLiving: value,
                // Если выбираем "в надлежащем состоянии" (value === true), очищаем deficiencies
                // Если выбираем "с недостатками" (value === false), инициализируем deficiencies, если их нет
                deficiencies: value ? null : (!safeHandoverItems.acceptance.deficiencies ? { list: '' } : safeHandoverItems.acceptance.deficiencies)
            }
        });
    };

    const handleAddDeficiencies = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            acceptance: {
                ...safeHandoverItems.acceptance,
                deficiencies: { list: '' }
            }
        });
    };

    const handleRemoveDeficiencies = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            acceptance: {
                ...safeHandoverItems.acceptance,
                deficiencies: null
            }
        });
    };

    const handleDeficienciesChange = (value) => {
        // Если deficiencies еще нет, создаем их вместе с изменением состояния
        if (!safeHandoverItems.acceptance.deficiencies) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                acceptance: {
                    ...safeHandoverItems.acceptance,
                    suitableForLiving: false,
                    deficiencies: { list: value }
                }
            });
        } else {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                acceptance: {
                    ...safeHandoverItems.acceptance,
                    deficiencies: {
                        ...safeHandoverItems.acceptance.deficiencies,
                        list: value
                    }
                }
            });
        }
    };

    const handleAddDefectsRepairDeadline = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            acceptance: {
                ...safeHandoverItems.acceptance,
                defectsRepairDeadline: { days: '5' }
            }
        });
    };

    const handleRemoveDefectsRepairDeadline = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            acceptance: {
                ...safeHandoverItems.acceptance,
                defectsRepairDeadline: null
            }
        });
    };

    const handleDefectsRepairDeadlineChange = (value) => {
        if (safeHandoverItems.acceptance.defectsRepairDeadline) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                acceptance: {
                    ...safeHandoverItems.acceptance,
                    defectsRepairDeadline: {
                        ...safeHandoverItems.acceptance.defectsRepairDeadline,
                        days: value
                    }
                }
            });
        }
    };

    // Обработчики для пункта 4 - Состояние помещения, оборудования и имущества
    const handlePropertyConditionToggle = () => {
        const newEnabled = !safeHandoverItems.propertyCondition.enabled;
        const hasAnySubItemEnabled = safeHandoverItems.propertyCondition.apartmentEnabled || 
                                    safeHandoverItems.propertyCondition.equipmentEnabled || 
                                    safeHandoverItems.propertyCondition.appliancesEnabled || 
                                    safeHandoverItems.propertyCondition.furnitureEnabled;
        
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                enabled: newEnabled,
                // Если включаем раздел и ни один подпункт не включен, включаем квартиру по умолчанию
                apartmentEnabled: newEnabled && !hasAnySubItemEnabled ? true : safeHandoverItems.propertyCondition.apartmentEnabled
            }
        });
    };

    const handlePropertyConditionChange = (field, value) => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                [field]: value
            }
        });
    };

    // Обработчики для переключения отдельных пунктов состояния
    const handleApartmentToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                apartmentEnabled: !safeHandoverItems.propertyCondition.apartmentEnabled
            }
        });
    };

    const handleEquipmentToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                equipmentEnabled: !safeHandoverItems.propertyCondition.equipmentEnabled
            }
        });
    };

    const handleAppliancesToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                appliancesEnabled: !safeHandoverItems.propertyCondition.appliancesEnabled
            }
        });
    };

    const handleFurnitureToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            propertyCondition: {
                ...safeHandoverItems.propertyCondition,
                furnitureEnabled: !safeHandoverItems.propertyCondition.furnitureEnabled
            }
        });
    };

    // Обработчики для пункта 5 - Ключи и документация
    const handleKeysAndDocumentsToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            keysAndDocuments: {
                ...safeHandoverItems.keysAndDocuments,
                enabled: !safeHandoverItems.keysAndDocuments.enabled
            }
        });
    };

    const handleKeysChange = (value) => {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                keysAndDocuments: {
                    ...safeHandoverItems.keysAndDocuments,
                keys: { count: value }
                }
            });
    };

    const handleAddDocumentation = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            keysAndDocuments: {
                ...safeHandoverItems.keysAndDocuments,
                documentation: { description: '' }
            }
        });
    };

    const handleRemoveDocumentation = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            keysAndDocuments: {
                ...safeHandoverItems.keysAndDocuments,
                documentation: null
            }
        });
    };

    const handleDocumentationChange = (value) => {
        if (safeHandoverItems.keysAndDocuments.documentation) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                keysAndDocuments: {
                    ...safeHandoverItems.keysAndDocuments,
                    documentation: { description: value }
                }
            });
        }
    };

    // Обработчики для пункта 6 - Показания приборов учета
    const handleMeterReadingsToggle = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                enabled: !safeHandoverItems.meterReadings.enabled
            }
        });
    };

    const handleAddElectricityMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                electricity: { readings: '', number: '' }
            }
        });
    };

    const handleRemoveElectricityMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                electricity: null
            }
        });
    };

    const handleElectricityMeterChange = (field, value) => {
        if (safeHandoverItems.meterReadings.electricity) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                meterReadings: {
                    ...safeHandoverItems.meterReadings,
                    electricity: {
                        ...safeHandoverItems.meterReadings.electricity,
                        [field]: value
                    }
                }
            });
        }
    };

    const handleAddHotWaterMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                hotWater: { readings: '', number: '' }
            }
        });
    };

    const handleRemoveHotWaterMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                hotWater: null
            }
        });
    };

    const handleHotWaterMeterChange = (field, value) => {
        if (safeHandoverItems.meterReadings.hotWater) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                meterReadings: {
                    ...safeHandoverItems.meterReadings,
                    hotWater: {
                        ...safeHandoverItems.meterReadings.hotWater,
                        [field]: value
                    }
                }
            });
        }
    };

    const handleAddColdWaterMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                coldWater: { readings: '', number: '' }
            }
        });
    };

    const handleRemoveColdWaterMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                coldWater: null
            }
        });
    };

    const handleColdWaterMeterChange = (field, value) => {
        if (safeHandoverItems.meterReadings.coldWater) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                meterReadings: {
                    ...safeHandoverItems.meterReadings,
                    coldWater: {
                        ...safeHandoverItems.meterReadings.coldWater,
                        [field]: value
                    }
                }
            });
        }
    };

    const handleAddGasMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                gas: { readings: '', number: '' }
            }
        });
    };

    const handleRemoveGasMeter = () => {
        handleHandoverItemsChange({
            ...safeHandoverItems,
            meterReadings: {
                ...safeHandoverItems.meterReadings,
                gas: null
            }
        });
    };

    const handleGasMeterChange = (field, value) => {
        if (safeHandoverItems.meterReadings.gas) {
            handleHandoverItemsChange({
                ...safeHandoverItems,
                meterReadings: {
                    ...safeHandoverItems.meterReadings,
                    gas: {
                        ...safeHandoverItems.meterReadings.gas,
                        [field]: value
                    }
                }
            });
        }
    };

    // Функция для автоматического определения количества экземпляров на основе срока договора
    const getAutomaticCopiesCount = () => {
        const durationMonths = term.durationMonths || 0;
        // Если срок менее 12 месяцев - 2 экземпляра, если 12 месяцев и более - 3 экземпляра
        return durationMonths >= 12 ? '3' : '2';
    };

    const automaticCopiesCount = getAutomaticCopiesCount();

    // Функция для динамической нумерации пунктов
    const getDynamicNumbering = () => {
        let currentNumber = 3; // Начинаем с 3 (пункты 1-2 = преамбула и описание помещения)
        const numbering = {
            acceptance: null,
            propertyCondition: null,
            keysAndDocuments: null,
            meterReadings: null,
            securityPayment: null,
            finalProvisions: null
        };

        // Пункт 3: Результат приемки (ВСЕГДА включен)
        numbering.acceptance = currentNumber++;

        // Пункт 4: Состояние помещения (ОПЦИОНАЛЬНО)
        if (safeHandoverItems.propertyCondition.enabled) {
            numbering.propertyCondition = currentNumber++;
        }

        // Пункт 5: Ключи и документация (ОПЦИОНАЛЬНО)
        if (safeHandoverItems.keysAndDocuments.enabled) {
            numbering.keysAndDocuments = currentNumber++;
        }

        // Пункт 6: Показания приборов учета (ОПЦИОНАЛЬНО)
        if (safeHandoverItems.meterReadings.enabled) {
            numbering.meterReadings = currentNumber++;
        }

        // Пункт 7: Обеспечительный платеж (ОПЦИОНАЛЬНО)
        if (mainContractSecurityPayment.enabled) {
            numbering.securityPayment = currentNumber++;
        }

        // Пункт 8-9: Заключительные положения (ВСЕГДА включены)
        numbering.finalProvisions = currentNumber++;

        return numbering;
    };

    const numbering = getDynamicNumbering();

    // Получаем номер приложения динамически
    const appendixNumber = getHandoverActNumber();

    return (
        <div className="document-preview-editor handover-act" style={{ marginTop: '60px', pageBreakBefore: 'always' }}>
            {/* Заголовок приложения */}
            <div style={{ textAlign: 'right', marginBottom: '20px', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ margin: '0 0 5px 0' }}>Приложение № {appendixNumber}</div>
                <div style={{ margin: '0 0 5px 0' }}>
                    к Договору найма жилого помещения № <InteractiveField
                        value={header.number}
                        onChange={(v) => handleHeaderChange('number', v)}
                        placeholder="5716"
                        isRequired={true}
                        showValidation={showValidation}
                        validationFieldId="handoverAct-headerNumber"
                    />
                </div>
                <div style={{ margin: '0' }}>
                    от <DateField
                        value={header.date}
                        onChange={(v) => handleHeaderChange('date', v)}
                        placeholder="дд.мм.гггг"
                        isRequired={true}
                        showValidation={showValidation}
                        validationFieldId="handoverAct-headerDate"
                    /> г.
                </div>
            </div>

            {/* Название акта */}
            <div className="document-title-block">
                <h3>Акт</h3>
                <p>приема-передачи жилого помещения.</p>
            </div>

            {/* Дата и место составления акта */}
            <div className="document-location-date">
                <InteractiveField
                    value={header.city}
                    onChange={(v) => handleHeaderChange('city', v)}
                    placeholder="г. Москва"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="handoverAct-headerCity"
                />
                <DateField
                    value={header.date}
                    onChange={(v) => handleHeaderChange('date', v)}
                    placeholder="дд.мм.гггг"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="handoverAct-headerDate"
                />
            </div>

            {/* Преамбула с данными сторон */}
            <p className="document-preamble">
                Гражданин РФ <InteractiveField
                    value={parties.landlordName}
                    onChange={(v) => handlePartiesChange('landlordName', v)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                    showValidation={showValidation}
                />,
                паспорт серии <InteractiveField
                    value={parties.landlordPassportSerial}
                    onChange={(v) => handlePartiesChange('landlordPassportSerial', v)}
                    placeholder="4512"
                    className="passport-serial-mask"
                    isRequired={true}
                    showValidation={showValidation}
                />
                {' '}№ <InteractiveField
                    value={parties.landlordPassportNumber}
                    onChange={(v) => handlePartiesChange('landlordPassportNumber', v)}
                    placeholder="654321"
                    className="passport-number-mask"
                    isRequired={true}
                    showValidation={showValidation}
                />,
                именуемый(ая) в дальнейшем "Наймодатель", с одной стороны и Гражданин РФ <InteractiveField
                    value={parties.tenantName}
                    onChange={(v) => handlePartiesChange('tenantName', v)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                    showValidation={showValidation}
                />,
                паспорт серии <InteractiveField
                    value={parties.tenantPassportSerial}
                    onChange={(v) => handlePartiesChange('tenantPassportSerial', v)}
                    placeholder="4509"
                    className="passport-serial-mask"
                    isRequired={true}
                    showValidation={showValidation}
                />
                {' '}№ <InteractiveField
                    value={parties.tenantPassportNumber}
                    onChange={(v) => handlePartiesChange('tenantPassportNumber', v)}
                    placeholder="123456"
                    className="passport-number-mask"
                    isRequired={true}
                    showValidation={showValidation}
                />,
                именуемый(ая) в дальнейшем "Наниматель", с другой стороны,                 согласно условиям Договора найма жилого помещения № <InteractiveField
                    value={header.number}
                    onChange={(v) => handleHeaderChange('number', v)}
                    placeholder="5716"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="handoverAct-headerNumber"
                /> от <DateField
                    value={header.date}
                    onChange={(v) => handleHeaderChange('date', v)}
                    placeholder="дд.мм.гггг"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="handoverAct-headerDate"
                    compact
                /> (далее - Договор) составили настоящий Акт о нижеследующем:
            </p>

            {/* Пункт 1 */}
            <p className="document-preamble">
                1. Наймодатель передал для проживания свободное изолированное жилое помещение, квартиру (далее - Помещение), а Наниматель принял данное Помещение во владение и пользование для проживания в нем.
            </p>

            {/* Пункт 2 - Описание помещения */}
            <p className="document-preamble">
                2. Помещение располагается в многоквартирном доме по адресу: <InteractiveField
                    value={property.address}
                    onChange={(v) => handlePropertyChange('address', v)}
                    placeholder="г. Москва, ул. Центральная, д. 10, кв. 55"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="property-address"
                />, в <InteractiveField
                    value={property.buildingFloors}
                    onChange={(v) => handlePropertyChange('buildingFloors', v)}
                    placeholder="5"
                    type="number"
                    showValidation={showValidation}
                    validationFieldId="property-buildingFloors"
                />-этажном доме на <InteractiveField
                    value={property.apartmentFloor}
                    onChange={(v) => handlePropertyChange('apartmentFloor', v)}
                    placeholder="3"
                    type="number"
                    showValidation={showValidation}
                    validationFieldId="property-apartmentFloor"
                />-м этаже.
            </p>

            <p className="document-preamble">
                Параметры Помещения:
            </p>
            <p className="document-preamble">
                квартира: <InteractiveField
                    value={property.roomCount}
                    onChange={(v) => handlePropertyChange('roomCount', v)}
                    placeholder="2"
                    type="number"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="property-roomCount"
                />-комнатная;
            </p>
            <p className="document-preamble">
                общая площадь <InteractiveField
                    value={property.totalArea}
                    onChange={(v) => handlePropertyChange('totalArea', v)}
                    placeholder="55.5"
                    type="number"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="property-totalArea"
                /> кв. м;
            </p>

            <div className="optional-field-block dynamic-content-block active-green">
                <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                    <button
                        className={`option-pill-toggle ${property.includeLivingArea ? 'selected' : ''}`}
                        onClick={() => handlePropertyToggle('includeLivingArea')}
                    >
                        {property.includeLivingArea ? '✓ Жилая площадь' : '+ Добавить жилую площадь'}
                    </button>
                </div>
                {property.includeLivingArea && (
                    <p className="document-preamble">
                        жилая площадь <InteractiveField
                            value={property.livingArea}
                            onChange={(v) => handlePropertyChange('livingArea', v)}
                            placeholder="35.0"
                            type="number"
                            isRequired={true}
                            showValidation={showValidation}
                        /> кв. м;
                    </p>
                )}
            </div>

            <div className="optional-field-block dynamic-content-block active-green">
                <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                    <button
                        className={`option-pill-toggle ${property.includeCadastralNumber ? 'selected' : ''}`}
                        onClick={() => handlePropertyToggle('includeCadastralNumber')}
                    >
                        {property.includeCadastralNumber ? '✓ Кадастровый номер' : '+ Добавить кадастровый номер'}
                    </button>
                </div>
                {property.includeCadastralNumber && (
                    <p className="document-preamble">
                        кадастровый номер квартиры: <InteractiveField
                            value={property.cadastralNumber}
                            onChange={(v) => handlePropertyChange('cadastralNumber', v)}
                            placeholder="77:01:0001001:1234"
                            isRequired={true}
                            showValidation={showValidation}
                        />;
                    </p>
                )}
            </div>

            <div className="optional-field-block dynamic-content-block active-green">
                <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                    <button
                        className={`option-pill-toggle ${property.includeOtherParameters ? 'selected' : ''}`}
                        onClick={() => handlePropertyToggle('includeOtherParameters')}
                    >
                        {property.includeOtherParameters ? '✓ Иные параметры' : '+ Добавить иные параметры'}
                    </button>
                </div>
                {property.includeOtherParameters && (
                    <p className="document-preamble">
                        иные параметры помещения: <MultilineField
                            value={property.otherParameters || ''}
                            onChange={(v) => handlePropertyChange('otherParameters', v)}
                            placeholder="пропишите дополнительные параметры помещения"
                            isRequired={true}
                            showValidation={showValidation}
                            minRows={1}
                            maxRows={5}
                        />
                    </p>
                )}
            </div>

            {/* Пункт 3 - Результат приемки Помещения */}
            <div className="dynamic-content-block active-purple">
                {/* Текст пункта в зависимости от выбора */}
                {safeHandoverItems.acceptance.suitableForLiving && !safeHandoverItems.acceptance.deficiencies ? (
                    <p className="document-preamble">
                        {numbering.acceptance}. Помещение пригодно для проживания и соответствует условиям Договора.
                    </p>
                ) : (
                    <>
                        <p className="document-preamble">
                            {numbering.acceptance}. Помещение принято со следующими недостатками, не препятствующими началу использования помещения:
                        </p>
                        <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                            <MultilineField
                                value={safeHandoverItems.acceptance.deficiencies?.list || ''}
                                onChange={handleDeficienciesChange}
                                placeholder="пропишите объекты и их недостатки"
                                isRequired={true}
                                showValidation={showValidation}
                                validationFieldId="handoverAct-deficienciesList"
                                minRows={1}
                                maxRows={8}
                            />
                        </div>
                        {/* Кнопка "Сроки устранения недостатков" - выше текста, который она добавляет */}
                        <div className="option-pills-group" style={{ marginTop: '12px', justifyContent: 'flex-start' }}>
                            <button
                                className={`option-pill-toggle ${safeHandoverItems.acceptance.defectsRepairDeadline ? 'selected' : ''}`}
                                onClick={safeHandoverItems.acceptance.defectsRepairDeadline ? handleRemoveDefectsRepairDeadline : handleAddDefectsRepairDeadline}
                            >
                                {safeHandoverItems.acceptance.defectsRepairDeadline ? '✓ Сроки устранения недостатков' : '+ Сроки устранения недостатков'}
                            </button>
                        </div>
                        {/* Сроки устранения недостатков внутри пункта - появляется после кнопки */}
                        {safeHandoverItems.acceptance.defectsRepairDeadline && (
                            <p className="document-preamble" style={{ marginTop: '12px' }}>
                                Сроки устранения недостатков Наймодателем: в течение <InteractiveField
                                    value={safeHandoverItems.acceptance.defectsRepairDeadline.days}
                                    onChange={handleDefectsRepairDeadlineChange}
                                    placeholder="5"
                                    type="number"
                                    isRequired={true}
                                    showValidation={showValidation}
                                    validationFieldId="handoverAct-defectsRepairDeadline"
                                    style={{ minWidth: '50px', maxWidth: '80px' }}
                                /> дней с момента подписания настоящего акта.
                            </p>
                        )}
                    </>
                )}

                {/* Кнопки управления - всегда видны под текстом */}
                <div className="option-pills-group" style={{ marginTop: '16px', justifyContent: 'flex-start' }}>
                    {/* Фиолетовые кнопки выбора */}
                    <button
                        className={`option-pill ${safeHandoverItems.acceptance.suitableForLiving && !safeHandoverItems.acceptance.deficiencies ? 'selected' : ''}`}
                        onClick={() => handleSuitableForLivingChange(true)}
                    >
                        {safeHandoverItems.acceptance.suitableForLiving && !safeHandoverItems.acceptance.deficiencies ? '✓' : ''} В надлежащем состоянии
                    </button>
                    <button
                        className={`option-pill ${!safeHandoverItems.acceptance.suitableForLiving && safeHandoverItems.acceptance.deficiencies ? 'selected' : ''}`}
                        onClick={() => handleSuitableForLivingChange(false)}
                    >
                        {!safeHandoverItems.acceptance.suitableForLiving && safeHandoverItems.acceptance.deficiencies ? '✓' : ''} Перечень недостатков
                    </button>
                </div>
            </div>

            {/* Пункт 4 - Состояние Помещения, оборудования и передаваемого имущества (ОПЦИОНАЛЬНО) */}
            <div className={`configurable-section ${safeHandoverItems.propertyCondition.enabled ? 'dynamic-content-block active-green' : ''}`} style={{ marginTop: '20px' }}>
                <div className="option-pills-group" style={{ marginBottom: '16px', justifyContent: 'flex-start' }}>
                    <button 
                        className={`option-pill-toggle ${safeHandoverItems.propertyCondition.enabled ? 'selected' : ''}`} 
                        onClick={handlePropertyConditionToggle}
                    >
                        {safeHandoverItems.propertyCondition.enabled ? '✓ Состояние Помещения, оборудования и передаваемого имущества' : '+ Состояние Помещения, оборудования и передаваемого имущества'}
                    </button>
                </div>

            {safeHandoverItems.propertyCondition.enabled && (
                <div>
                    <p className="document-preamble" style={{ marginTop: '16px' }}>
                        {numbering.propertyCondition}. Состояние Помещения, оборудования и передаваемого имущества:
                    </p>

                    {/* Квартира (состояние) */}
                    <div className="optional-field-block" style={{ marginTop: '12px' }}>
                        <div className="option-pills-group" style={{ marginBottom: '8px', justifyContent: 'flex-start' }}>
                            <button
                                className={`option-pill-toggle ${safeHandoverItems.propertyCondition.apartmentEnabled ? 'selected' : ''}`}
                                onClick={handleApartmentToggle}
                            >
                                {safeHandoverItems.propertyCondition.apartmentEnabled ? '✓ Квартира (состояние)' : '+ Квартира (состояние)'}
                            </button>
                        </div>
                        {safeHandoverItems.propertyCondition.apartmentEnabled && (
                            <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                                <MultilineField
                                    value={safeHandoverItems.propertyCondition.apartment}
                                    onChange={(value) => handlePropertyConditionChange('apartment', value)}
                                    placeholder="опишите состояние квартиры"
                                    isRequired={true}
                                    showValidation={showValidation}
                                    validationFieldId="handoverAct-apartmentCondition"
                                    minRows={2}
                                    maxRows={4}
                                />
                            </div>
                        )}
                    </div>

                    {/* Оборудование (перечень и состояние) */}
                    <div className="optional-field-block" style={{ marginTop: '12px' }}>
                        <div className="option-pills-group" style={{ marginBottom: '8px', justifyContent: 'flex-start' }}>
                            <button
                                className={`option-pill-toggle ${safeHandoverItems.propertyCondition.equipmentEnabled ? 'selected' : ''}`}
                                onClick={handleEquipmentToggle}
                            >
                                {safeHandoverItems.propertyCondition.equipmentEnabled ? '✓ Оборудование (перечень и состояние)' : '+ Оборудование (перечень и состояние)'}
                            </button>
                        </div>
                        {safeHandoverItems.propertyCondition.equipmentEnabled && (
                            <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                                <MultilineField
                                    value={safeHandoverItems.propertyCondition.equipment}
                                    onChange={(value) => handlePropertyConditionChange('equipment', value)}
                                    placeholder="пропишите перечень и состояние оборудования"
                                    isRequired={true}
                                    showValidation={showValidation}
                                    validationFieldId="handoverAct-equipmentCondition"
                                    minRows={2}
                                    maxRows={4}
                                />
                            </div>
                        )}
                    </div>

                    {/* Бытовая техника (перечень и состояние) */}
                    <div className="optional-field-block" style={{ marginTop: '12px' }}>
                        <div className="option-pills-group" style={{ marginBottom: '8px', justifyContent: 'flex-start' }}>
                            <button
                                className={`option-pill-toggle ${safeHandoverItems.propertyCondition.appliancesEnabled ? 'selected' : ''}`}
                                onClick={handleAppliancesToggle}
                            >
                                {safeHandoverItems.propertyCondition.appliancesEnabled ? '✓ Бытовая техника (перечень и состояние)' : '+ Бытовая техника (перечень и состояние)'}
                            </button>
                        </div>
                        {safeHandoverItems.propertyCondition.appliancesEnabled && (
                            <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                                <MultilineField
                                    value={safeHandoverItems.propertyCondition.appliances}
                                    onChange={(value) => handlePropertyConditionChange('appliances', value)}
                                    placeholder="пропишите перечень и состояние бытовой техники"
                                    isRequired={true}
                                    showValidation={showValidation}
                                    validationFieldId="handoverAct-appliancesCondition"
                                    minRows={2}
                                    maxRows={4}
                                />
                            </div>
                        )}
                    </div>

                    {/* Мебель (перечень и состояние) */}
                    <div className="optional-field-block" style={{ marginTop: '12px' }}>
                        <div className="option-pills-group" style={{ marginBottom: '8px', justifyContent: 'flex-start' }}>
                            <button
                                className={`option-pill-toggle ${safeHandoverItems.propertyCondition.furnitureEnabled ? 'selected' : ''}`}
                                onClick={handleFurnitureToggle}
                            >
                                {safeHandoverItems.propertyCondition.furnitureEnabled ? '✓ Мебель (перечень и состояние)' : '+ Мебель (перечень и состояние)'}
                            </button>
                        </div>
                        {safeHandoverItems.propertyCondition.furnitureEnabled && (
                            <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                                <MultilineField
                                    value={safeHandoverItems.propertyCondition.furniture}
                                    onChange={(value) => handlePropertyConditionChange('furniture', value)}
                                    placeholder="пропишите перечень и состояние мебели"
                                    isRequired={true}
                                    showValidation={showValidation}
                                    validationFieldId="handoverAct-furnitureCondition"
                                    minRows={2}
                                    maxRows={4}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            </div>


            {/* Пункт 5 - Приемка ключей и документации (ОПЦИОНАЛЬНО) */}
            <div className={`configurable-section ${safeHandoverItems.keysAndDocuments.enabled ? 'dynamic-content-block active-green' : ''}`} style={{ marginTop: '20px' }}>
                <div className="option-pills-group" style={{ marginBottom: '16px', justifyContent: 'flex-start' }}>
                    <button 
                        className={`option-pill-toggle ${safeHandoverItems.keysAndDocuments.enabled ? 'selected' : ''}`} 
                        onClick={handleKeysAndDocumentsToggle}
                    >
                        {safeHandoverItems.keysAndDocuments.enabled ? '✓ Приемка ключей и документации' : '+ Приемка ключей и документации'}
                    </button>
                </div>

            {safeHandoverItems.keysAndDocuments.enabled && (
                <div>
                    <p className="document-preamble" style={{ marginTop: '16px' }}>
                        {numbering.keysAndDocuments}. Наймодатель также с Помещением передает:
                    </p>

            {/* Ключи */}
                    <p className="document-preamble">
                        ключи: <InteractiveField
                            value={safeHandoverItems.keysAndDocuments.keys.count}
                            onChange={handleKeysChange}
                            placeholder="цифра"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-keysCount"
                            style={{ minWidth: '80px' }}
                        /> {pluralizeExemplars(safeHandoverItems.keysAndDocuments.keys.count)};
                    </p>

            {/* Документация */}
            <div className="optional-field-block">
                <button
                    className={`option-pill-toggle ${safeHandoverItems.keysAndDocuments.documentation ? 'selected' : ''}`}
                    onClick={safeHandoverItems.keysAndDocuments.documentation ? handleRemoveDocumentation : handleAddDocumentation}
                    style={{ marginTop: '12px', marginBottom: '8px' }}
                >
                    {safeHandoverItems.keysAndDocuments.documentation ? '✓ Документация' : '+ Документация'}
                </button>
                {safeHandoverItems.keysAndDocuments.documentation && (
                    <>
                                <p className="document-preamble" style={{ marginTop: '12px' }}>
                            документацию:
                        </p>
                        <div className="block-textarea-wrapper" style={{ marginTop: '8px' }}>
                            <MultilineField
                                value={safeHandoverItems.keysAndDocuments.documentation.description}
                                        onChange={handleDocumentationChange}
                                placeholder="пропишите документацию, подлежащую передаче (например: техпаспорт на кондиционер, инструкция к бойлеру)"
                                isRequired={true}
                                showValidation={showValidation}
                                validationFieldId="handoverAct-documentation"
                                minRows={1}
                                maxRows={3}
                            />
                        </div>
                    </>
                )}
            </div>
                </div>
            )}
            </div>


            {/* Пункт 6 - Показания приборов учета (ОПЦИОНАЛЬНО) */}
            <div className={`configurable-section ${safeHandoverItems.meterReadings.enabled ? 'dynamic-content-block active-green' : ''}`} style={{ marginTop: '20px' }}>
                <div className="option-pills-group" style={{ marginBottom: '16px', justifyContent: 'flex-start' }}>
                    <button 
                        className={`option-pill-toggle ${safeHandoverItems.meterReadings.enabled ? 'selected' : ''}`} 
                        onClick={handleMeterReadingsToggle}
                    >
                        {safeHandoverItems.meterReadings.enabled ? '✓ Показания приборов учета' : '+ Показания приборов учета'}
                    </button>
                </div>

            {safeHandoverItems.meterReadings.enabled && (
                <div>
                    <p className="document-preamble" style={{ marginTop: '16px' }}>
                        {numbering.meterReadings}. Показания приборов учета:
                    </p>

            {/* Электроэнергия */}
            <div className="optional-field-block">
                <button
                    className={`option-pill-toggle ${safeHandoverItems.meterReadings.electricity ? 'selected' : ''}`}
                    onClick={safeHandoverItems.meterReadings.electricity ? handleRemoveElectricityMeter : handleAddElectricityMeter}
                    style={{ marginTop: '12px', marginBottom: '8px' }}
                >
                    {safeHandoverItems.meterReadings.electricity ? '✓ Электроэнергия' : '+ Электроэнергия'}
                </button>
                {safeHandoverItems.meterReadings.electricity && (
                    <p className="document-preamble">
                        электроэнергия: <InteractiveField
                            value={safeHandoverItems.meterReadings.electricity.readings}
                            onChange={(value) => handleElectricityMeterChange('readings', value)}
                            placeholder="показания прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-electricityMeter"
                            style={{ minWidth: '200px' }}
                        /> прибор учета № <InteractiveField
                            value={safeHandoverItems.meterReadings.electricity.number}
                            onChange={(value) => handleElectricityMeterChange('number', value)}
                            placeholder="номер прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-electricityMeter"
                            style={{ minWidth: '180px' }}
                        />.
                    </p>
                )}
            </div>

            {/* Горячая вода */}
            <div className="optional-field-block">
                <button
                    className={`option-pill-toggle ${safeHandoverItems.meterReadings.hotWater ? 'selected' : ''}`}
                    onClick={safeHandoverItems.meterReadings.hotWater ? handleRemoveHotWaterMeter : handleAddHotWaterMeter}
                    style={{ marginTop: '12px', marginBottom: '8px' }}
                >
                    {safeHandoverItems.meterReadings.hotWater ? '✓ Горячая вода' : '+ Горячая вода'}
                </button>
                {safeHandoverItems.meterReadings.hotWater && (
                    <p className="document-preamble">
                        горячая вода: <InteractiveField
                            value={safeHandoverItems.meterReadings.hotWater.readings}
                            onChange={(value) => handleHotWaterMeterChange('readings', value)}
                            placeholder="показания прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-hotWaterMeter"
                            style={{ minWidth: '200px' }}
                        /> прибор учета № <InteractiveField
                            value={safeHandoverItems.meterReadings.hotWater.number}
                            onChange={(value) => handleHotWaterMeterChange('number', value)}
                            placeholder="номер прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-hotWaterMeter"
                            style={{ minWidth: '180px' }}
                        />.
                    </p>
                )}
            </div>

            {/* Холодная вода */}
            <div className="optional-field-block">
                <button
                    className={`option-pill-toggle ${safeHandoverItems.meterReadings.coldWater ? 'selected' : ''}`}
                    onClick={safeHandoverItems.meterReadings.coldWater ? handleRemoveColdWaterMeter : handleAddColdWaterMeter}
                    style={{ marginTop: '12px', marginBottom: '8px' }}
                >
                    {safeHandoverItems.meterReadings.coldWater ? '✓ Холодная вода' : '+ Холодная вода'}
                </button>
                {safeHandoverItems.meterReadings.coldWater && (
                    <p className="document-preamble">
                        холодная вода: <InteractiveField
                            value={safeHandoverItems.meterReadings.coldWater.readings}
                            onChange={(value) => handleColdWaterMeterChange('readings', value)}
                            placeholder="показания прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-coldWaterMeter"
                            style={{ minWidth: '200px' }}
                        /> прибор учета № <InteractiveField
                            value={safeHandoverItems.meterReadings.coldWater.number}
                            onChange={(value) => handleColdWaterMeterChange('number', value)}
                            placeholder="номер прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-coldWaterMeter"
                            style={{ minWidth: '180px' }}
                        />.
                    </p>
                )}
            </div>

            {/* Газ */}
            <div className="optional-field-block">
                <button
                    className={`option-pill-toggle ${safeHandoverItems.meterReadings.gas ? 'selected' : ''}`}
                    onClick={safeHandoverItems.meterReadings.gas ? handleRemoveGasMeter : handleAddGasMeter}
                    style={{ marginTop: '12px', marginBottom: '8px' }}
                >
                    {safeHandoverItems.meterReadings.gas ? '✓ Газ' : '+ Газ'}
                </button>
                {safeHandoverItems.meterReadings.gas && (
                    <p className="document-preamble">
                        газ: <InteractiveField
                            value={safeHandoverItems.meterReadings.gas.readings}
                            onChange={(value) => handleGasMeterChange('readings', value)}
                            placeholder="показания прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-gasMeter"
                            style={{ minWidth: '200px' }}
                        /> прибор учета № <InteractiveField
                            value={safeHandoverItems.meterReadings.gas.number}
                            onChange={(value) => handleGasMeterChange('number', value)}
                            placeholder="номер прибора"
                            isRequired={true}
                            showValidation={showValidation}
                            validationFieldId="handoverAct-gasMeter"
                            style={{ minWidth: '180px' }}
                        />.
                    </p>
                )}
            </div>
                </div>
            )}
            </div>

            {/* Пункт 7 - Обеспечительный платеж (ОПЦИОНАЛЬНО) */}
            <div className={`configurable-section ${mainContractSecurityPayment.enabled ? 'dynamic-content-block active-green' : ''}`} style={{ marginTop: '20px' }}>
                <div className="option-pills-group" style={{ marginBottom: '16px', justifyContent: 'flex-start' }}>
                    <button
                        className={`option-pill-toggle ${mainContractSecurityPayment.enabled ? 'selected' : ''}`}
                        onClick={() => handlePaymentChange('enabled', !mainContractSecurityPayment.enabled)}
                    >
                        {mainContractSecurityPayment.enabled ? '✓ Обеспечительный платеж внесен' : '+ Обеспечительный платеж внесен'}
                    </button>
                </div>
                {mainContractSecurityPayment.enabled && (
                    <div>
                        <p className="document-preamble" style={{ marginTop: '16px' }}>
                            {numbering.securityPayment}. Наниматель внес обеспечительный платеж в размере{' '}
                            <InteractiveField
                                value={mainContractSecurityPayment.amount}
                                onChange={(v) => handlePaymentChange('amount', v)}
                                placeholder="50000"
                                type="number"
                                integerOnly={true}
                                isRequired={true}
                                showValidation={showValidation}
                            />{' '}
                            {mainContractSecurityPayment.amount && (
                                <>
                                    <span className="amount-words-wrapper">
                                        (<span>
                                            {mainContractSecurityPayment.amountWords || convertAmountToWords(mainContractSecurityPayment.amount)}
                                        </span>)
                                    </span>
                                </>
                            )}{' '}
                            {getRublesForm(mainContractSecurityPayment.amount)}.
                        </p>
                    </div>
                )}
            </div>

            {/* Пункты 8-9 - Заключительные положения акта */}
            {automaticCopiesCount === '3' ? (
                <p className="document-preamble" style={{ marginTop: '20px' }}>
                    {numbering.finalProvisions}. Настоящий Акт составлен в трех экземплярах, имеющих равную юридическую силу, по одному для Сторон и один - для регистрирующего органа.
                </p>
            ) : (
                <p className="document-preamble" style={{ marginTop: '20px' }}>
                    {numbering.finalProvisions}. Настоящий Акт составлен в двух экземплярах, имеющих равную юридическую силу, по одному для каждой из Сторон.
                </p>
            )}

            <p className="document-preamble">
                {numbering.finalProvisions + 1}. Настоящий Акт является неотъемлемой частью Договора.
            </p>
        </div>
    );
};

export default HandoverActSection;
